import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  KibCloseFullscreenIcon,
  KibOpenFullscreenIcon,
} from '@chewy/kib-icons-react'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { PermissionArea } from '@pbt/pbt-ui-components'

import RhapTextButton from '~/components/common/RhapTextButton'
import FeatureToggle from '~/constants/featureToggle'
import { getChargeSheet } from '~/store/duck/clientFinanceData'
import { useGetChargesList } from '~/store/hooks/chargeSheet'
import {
  useHandleOrderRefill,
  useLogItemStateGetter,
  useOrderEditListItem,
} from '~/store/hooks/orders'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatientName } from '~/store/reducers/patients'
import { getClientId, getPatientId, getSoapId } from '~/store/reducers/soap'
import { getUser } from '~/store/reducers/users'
import useWSTopic, { WSTopics } from '~/utils/useWSTopic'

import { LineItemComponentsActions } from '../collapsibleRail/summary/orders/orderSummaryUtils'
import SoapWidget from '../SoapWidget'
import ChargesWidgetExpanded from './ChargesWidgetExpanded'
import ChargesSoapTable from './table/ChargesSoapTable'
import ChirpSoapChargesTable from './table/ChirpSoapChargesTable'
import ChargesItems from './tabs/ChargesItems'
import { useChargeOrderAlert } from './utils/useChargeOrderAlert'
import { useClearExpandedGroup } from './utils/useClearExpandedGroup'

const useStyles = makeStyles(
  () => ({
    content: {
      flexDirection: 'column-reverse',
    },
    containerNoBorder: {
      border: 'none',
    },
  }),
  { name: 'ChargesWidgetUnlocked' },
)

const ChargesWidgetUnlocked = () => {
  const classes = useStyles()
  const { t } = useTranslation('Payments')

  const soapId = useSelector(getSoapId)
  const patientId = useSelector(getPatientId)
  const clientId = useSelector(getClientId)
  const client = useSelector(getUser(clientId))
  const SOAPPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))
  const chargeSheet = useSelector(getChargeSheet)
  const isAwoChargesR5Epic5Enabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_CHARGES_R5_5),
  )
  const chargesList = useGetChargesList(clientId, soapId)

  const logItemStateGetter = useLogItemStateGetter()
  const { handleRefill, handleSavePrescription, openPrescriptionDialog } =
    useHandleOrderRefill(client, logItemStateGetter)
  const handleEditListItem = useOrderEditListItem()

  // Show alert when order can't be removed
  useChargeOrderAlert()
  // Expanded bundles from charges widget should be consistent with invoice
  useClearExpandedGroup()

  const [expandedTab, setExpandedTab] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const items = chargesList
  const editDisabled = !SOAPPermissions.update

  const handleExpandTab = useCallback((id: string | null) => {
    setExpandedTab(id)
  }, [])

  const patientName = useSelector(getPatientName(patientId))
  const displayPatientName = patientName || t('Common:PET')

  const handleExpandClick = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const renderChargesTable = (expanded = false) =>
    isAwoChargesR5Epic5Enabled ? (
      <ChirpSoapChargesTable
        chargesId={R.propOr('', 'id', chargeSheet)}
        editDisabled={editDisabled}
        isExpanded={expanded}
        items={items}
      />
    ) : (
      <ChargesSoapTable
        ComponentsActions={LineItemComponentsActions}
        chargesId={R.propOr('', 'id', chargeSheet)}
        editDisabled={editDisabled}
        items={items}
      />
    )

  useWSTopic({
    wsTopic: WSTopics.CHARGES_WIDGET_GROUPED_ITEM,
    context: {
      retrySubscription: false,
      topicParams: {
        soapId,
      },
    },
    unsubscribe: true,
  })

  useWSTopic({
    wsTopic: WSTopics.REMINDERS,
    context: {
      retrySubscription: true,
      topicParams: {
        patientId,
      },
    },
    unsubscribe: true,
  })

  useWSTopic({
    wsTopic: WSTopics.CHARGES_POSTED_ON_SOAP,
    context: {
      retrySubscription: false,
      topicParams: {
        soapId,
        clientId,
      },
    },
    unsubscribe: true,
  })

  useWSTopic({
    wsTopic: WSTopics.CHARGES_SPECIAL_PROCEDURE,
    context: {
      retrySubscription: false,
      topicParams: {
        patientId,
      },
    },
    unsubscribe: true,
  })

  return (
    <>
      <SoapWidget
        actions={
          isAwoChargesR5Epic5Enabled && (
            <RhapTextButton
              emphasis="secondary"
              icon={
                isExpanded ? (
                  <KibCloseFullscreenIcon />
                ) : (
                  <KibOpenFullscreenIcon />
                )
              }
              iconPosition="end"
              size="small"
              theme="action"
              onClick={handleExpandClick}
            >
              {isExpanded
                ? t('Common:COLLAPSE_ACTION')
                : t('Common:EXPAND_ACTION')}
            </RhapTextButton>
          )
        }
        classes={{
          content: classes.content,
          container: isAwoChargesR5Epic5Enabled
            ? classes.containerNoBorder
            : undefined,
        }}
        id="charges-widget"
        title={
          !isAwoChargesR5Epic5Enabled
            ? t('Payments:CHARGES')
            : t('Payments:CHARGES_WITH_PATIENT_NAME', {
                patientName: displayPatientName,
              })
        }
      >
        {!expandedTab && renderChargesTable()}
        <ChargesItems
          editDisabled={editDisabled}
          expandedTab={expandedTab}
          view="drop-down"
          onExpandTab={handleExpandTab}
        />
      </SoapWidget>
      {isExpanded && (
        <ChargesWidgetExpanded
          disabled={editDisabled}
          handleEditListItem={handleEditListItem}
          handleRefill={handleRefill}
          handleSavePrescription={handleSavePrescription}
          logItemStateGetter={logItemStateGetter}
          openPrescriptionDialog={openPrescriptionDialog}
          patientName={displayPatientName}
          renderChargesTable={renderChargesTable}
          onCollapse={handleExpandClick}
        />
      )}
    </>
  )
}

export default ChargesWidgetUnlocked
